import classNames from 'classnames';
import React from 'react';
import Badge from '../components/Badge';
import Button from '../components/Button';
import SVGIcon from '../components/SVGIcon';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated';
import hideIcon from '../icons/non-prefilled/16x16/hide.svg';
import readIcon from '../icons/non-prefilled/16x16/read.svg';
import retryIcon from '../icons/non-prefilled/16x16/retry.svg';
import NewReorderableList from './NewReorderableList';
import * as styles from './SectionList.module.scss';
import SidebarSectionIcon from './SidebarSectionIcon';
import { getSidebarSectionNameWithFallback } from './SidebarSectionsProvider';

const SectionList = ({
  children,
  ...props
}: Omit<React.ComponentProps<typeof NewReorderableList>, 'name'>) => {
  return (
    <NewReorderableList {...props}>
      <div className={styles.container}>{children}</div>
    </NewReorderableList>
  );
};

const SECTION_FRAGMENT = gql(`
  fragment SectionListRow_Section on Section {
    id
    name
    ...SidebarSectionIcon_section
  }
`);

interface SectionListRowProps {
  section: DocumentType<typeof SECTION_FRAGMENT>;
  visible: boolean;
  teamSection: boolean;
  selected?: boolean;
  deleted?: boolean;
  onClick?: () => void;
  onVisibilityButtonClick?: () => void;
  onRestoreButtonClick?: () => void;
  index: number;
}

const SectionListRow = ({
  section,
  selected,
  deleted,
  visible,
  teamSection,
  onClick,
  onVisibilityButtonClick,
  onRestoreButtonClick,
  index,
}: SectionListRowProps) => {
  type ActionIconButton = {
    icon: string;
    onClick?: () => void;
  };
  const actionIconButton: ActionIconButton = deleted
    ? {
        icon: retryIcon,
        onClick: onRestoreButtonClick,
      }
    : {
        icon: visible ? readIcon : hideIcon,
        onClick: onVisibilityButtonClick,
      };

  return (
    <NewReorderableList.Row index={index} className={styles.rowContainer}>
      {() => (
        <div
          className={classNames(styles.content, {
            [styles.isSelected]: selected,
            [styles.isVisible]: visible,
            [styles.isDeleted]: deleted,
          })}
        >
          <Button className={styles.contentButton} onClick={onClick}>
            <NewReorderableList.DragIndicator
              className={styles.contentButtonContent}
              index={index}
            >
              <span aria-hidden className={styles.icon}>
                <SidebarSectionIcon section={section} />
              </span>
              <span className={styles.name}>
                {getSidebarSectionNameWithFallback(section.name)}
              </span>
              <span className={styles.nameBadgeContainer}>
                {teamSection && !deleted && <Badge color="green">Team</Badge>}
              </span>
            </NewReorderableList.DragIndicator>
          </Button>
          {actionIconButton.onClick && (
            <Button
              className={styles.actionIconButton}
              onClick={actionIconButton.onClick}
            >
              <SVGIcon className={styles.actionIcon}>
                {actionIconButton.icon}
              </SVGIcon>
            </Button>
          )}
        </div>
      )}
    </NewReorderableList.Row>
  );
};

SectionList.Row = SectionListRow;

export default SectionList;
